import { useEffect, useRef, useState } from "react";
import { Download, Paperclip, RotateCcw } from "lucide-react";
import { ActionDialog, Button, EmailPreview, EmailStatusTag, EmptyState, Modal, NoticeBox } from "../ui";
import { downloadMailAttachmentAPI, getMailAPI, retryMailAPI } from "../../utils/api/mails";
import { SOURCE_LABELS, TYPE_LABELS, formatDateTime, labelFor } from "./mailText";

const formatSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`);

const Row = ({ label, children, ltr = false }) =>
  children ? (
    <>
      <dt>{label}</dt>
      <dd dir={ltr ? "ltr" : "auto"}>{children}</dd>
    </>
  ) : null;

// One email: preview, recipients, attachments, provider answer, timestamps and the attempt history.
export default function MailDetails({ id, lang, t, onClose, onChanged }) {
  const d = t.detail;
  const [mail, setMail] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [notice, setNotice] = useState(null);
  const [fileError, setFileError] = useState("");
  const tokenRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    tokenRef.current = token;
    const controller = new AbortController();
    setMail(null);
    setError("");
    getMailAPI(token, id, controller.signal)
      .then((result) => setMail(result.mail))
      .catch((failure) => {
        if (failure?.name !== "AbortError") setError(failure?.message || d.loadFailed);
      });
    return () => controller.abort();
  }, [id, d.loadFailed]);

  const download = async (file) => {
    setFileError("");
    try {
      await downloadMailAttachmentAPI(tokenRef.current, id, file.index, file.filename);
    } catch (failure) {
      setFileError(failure?.message || d.downloadFailed);
    }
  };

  const retry = async () => {
    const result = await retryMailAPI(tokenRef.current, id, { acknowledgeDuplicate: acknowledged });
    setAcknowledged(false);
    setMail(result.mail);
    setNotice(result.accepted ? { variant: "success", text: t.retryDialog.done } : { variant: "warning", text: t.retryDialog.stillFailed });
    setRetrying(false);
    onChanged?.();
  };

  const message = mail && {
    to: mail.to,
    cc: mail.cc,
    subject: mail.subject,
    html: mail.html,
    text: mail.text || (mail.bodyWithheld ? d.bodyWithheld : d.noBody),
  };
  const statusLabels = { status: { ...t.status, none: t.status.pending } };
  const help = mail && t.statusHelp[mail.status === "sending" ? "pending" : mail.status];

  return (
    <>
      <Modal
        title={mail?.subject || t.title}
        description={mail ? `${labelFor(TYPE_LABELS, lang, mail.type)} · ${labelFor(SOURCE_LABELS, lang, mail.source)}` : undefined}
        size="lg"
        closeLabel={d.close}
        onClose={onClose}
        busy={retrying}
        footer={
          <Button variant="neutral" appearance="outline" onClick={onClose}>
            {d.close}
          </Button>
        }
      >
        <div className="mail-detail" dir={lang === "ar" ? "rtl" : "ltr"}>
          {error ? (
            <EmptyState variant="error" compact>
              {error}
            </EmptyState>
          ) : !mail ? (
            <EmptyState variant="loading" compact>
              {t.loading}
            </EmptyState>
          ) : (
            <>
              <div className="mail-detail-status">
                <EmailStatusTag status={mail.status} labels={statusLabels} size="md" />
                {help && <p>{help}</p>}
              </div>
              {mail.lastError && mail.status !== "sent" && (
                <NoticeBox variant="danger" bordered title={d.error}>
                  <p dir="ltr">
                    {mail.lastError.code ? `${mail.lastError.code}: ` : ""}
                    {mail.lastError.message}
                  </p>
                </NoticeBox>
              )}
              {notice && (
                <NoticeBox variant={notice.variant} bordered>
                  <p>{notice.text}</p>
                </NoticeBox>
              )}
              {mail.bodyWithheld && (
                <NoticeBox variant="neutral" bordered>
                  <p>{d.bodyWithheld}</p>
                </NoticeBox>
              )}
              {mail.bodyTruncated && (
                <NoticeBox variant="neutral" bordered>
                  <p>{d.bodyTruncated}</p>
                </NoticeBox>
              )}

              <EmailPreview message={message} lang={lang} labels={{ to: d.to, cc: d.cc, subject: d.subject, attachments: d.attachments, body: d.body }} />

              {mail.attachments.length > 0 && (
                <section aria-label={d.attachments}>
                  <h4 className="mail-detail-heading">{d.attachments}</h4>
                  <ul className="mail-attachments">
                    {mail.attachments.map((file) => (
                      <li key={file.index}>
                        <Paperclip size={14} aria-hidden="true" />
                        <span dir="auto">{file.filename}</span>
                        <small>{formatSize(file.size)}</small>
                        {file.available ? (
                          <Button size="sm" appearance="soft" variant="primary" icon={<Download size={14} />} onClick={() => download(file)}>
                            {d.download}
                          </Button>
                        ) : (
                          <small className="mail-muted">{d.attachmentGone}</small>
                        )}
                      </li>
                    ))}
                  </ul>
                  {fileError && (
                    <p className="mail-error" role="alert">
                      {fileError}
                    </p>
                  )}
                </section>
              )}

              <dl className="mail-facts">
                <Row label={d.from} ltr>
                  {mail.from}
                </Row>
                {mail.bcc.length > 0 && (
                  <Row label={d.bcc} ltr>
                    {mail.bcc.join(", ")}
                  </Row>
                )}
                <Row label={d.store}>{mail.storeName || mail.storeId}</Row>
                <Row label={d.app}>{mail.application}</Row>
                <Row label={d.request}>{mail.related?.label || mail.related?.id}</Row>
                <Row label={d.initiatedBy}>{mail.initiatedBy?.name ? `${mail.initiatedBy.name}${mail.initiatedBy.role ? ` · ${mail.initiatedBy.role}` : ""}` : d.system}</Row>
                <Row label={d.created}>{formatDateTime(mail.createdAt, lang)}</Row>
                <Row label={d.sentAt}>{mail.sentAt ? formatDateTime(mail.sentAt, lang) : null}</Row>
                <Row label={d.updated}>{formatDateTime(mail.updatedAt, lang)}</Row>
                <Row label={d.providerStatus} ltr>
                  {mail.providerStatus}
                </Row>
                <Row label={d.providerId} ltr>
                  {mail.providerMessageId}
                </Row>
                <Row label={d.delivery}>{(d.deliveryStatuses && d.deliveryStatuses[mail.deliveryStatus]) || (mail.deliveryStatus === "unknown" ? d.deliveryUnknown : mail.deliveryStatus)}</Row>
                {mail.retry && (
                  <>
                    <Row label={d.attemptsUsed}>{`${mail.retry.attemptsUsed} / ${mail.retry.maxAttempts}`}</Row>
                    <Row label={d.lastAttempt}>{mail.attempts.length ? formatDateTime(mail.attempts[mail.attempts.length - 1].finishedAt || mail.attempts[mail.attempts.length - 1].startedAt, lang) : null}</Row>
                    <Row label={d.nextRetry}>{mail.retry.nextRetryAt ? formatDateTime(mail.retry.nextRetryAt, lang) : null}</Row>
                    <Row label={d.retryOwner}>{d.owners[mail.retry.owner]}</Row>
                  </>
                )}
              </dl>

              <section aria-label={d.attempts}>
                <h4 className="mail-detail-heading">{d.attempts}</h4>
                <ol className="mail-attempts">
                  {mail.attempts.map((attempt) => (
                    <li key={attempt.number}>
                      <strong>
                        {d.attempt} {attempt.number}
                      </strong>
                      <span>{d.results[attempt.result] || attempt.result}</span>
                      {attempt.auto && <small>{d.autoAttempt}</small>}
                      {attempt.acknowledgedDuplicate && <small>{d.acknowledgedAttempt}</small>}
                      <small>{formatDateTime(attempt.finishedAt || attempt.startedAt, lang)}</small>
                      {attempt.by?.name && (
                        <small>
                          {attempt.by.name}
                          {attempt.by.role ? ` · ${attempt.by.role}` : ""}
                        </small>
                      )}
                      {attempt.error && (
                        <small className="mail-error" dir="ltr">
                          {attempt.errorCode ? `${attempt.errorCode}: ` : ""}
                          {attempt.error}
                        </small>
                      )}
                    </li>
                  ))}
                </ol>
              </section>

              {["failed", "uncertain", "skipped"].includes(mail.status) && (
                <div className="mail-retry">
                  {mail.retry?.reason && d.reasons[mail.retry.reason] && <p className="mail-muted">{d.reasons[mail.retry.reason]}</p>}
                  {mail.canRetry ? (
                    <>
                      {mail.status === "uncertain" && (
                        <label className="mail-ack">
                          <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} /> {d.acknowledge}
                        </label>
                      )}
                      <Button variant="primary" icon={<RotateCcw size={15} />} disabled={mail.status === "uncertain" && !acknowledged} onClick={() => setRetrying(true)}>
                        {d.retry}
                      </Button>
                    </>
                  ) : (
                    <p className="mail-muted">{mail.retry?.owner === "flow" || mail.retryOwner === "flow" ? d.retryFlow : d.retryUnavailable}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
      {retrying && (
        <ActionDialog
          title={t.retryDialog.title}
          description={t.retryDialog.description}
          confirmLabel={t.retryDialog.confirm}
          labels={{ confirm: t.retryDialog.confirm, cancel: t.retryDialog.cancel, failed: t.retryDialog.failed }}
          onConfirm={retry}
          onClose={() => setRetrying(false)}
        />
      )}
    </>
  );
}
